import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircularProgress, Stack, Typography } from '@mui/material';
import { resolveUntisLesson } from 'api/untis-timetable';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getLessonUrl, HOME } from 'utils/links';
import { log, Severity } from 'utils/logger';
/**
 * UntisRedirect resolves an Untis period_id to a Skyla lesson and redirects.
 *
 * This page is the target of deep-links from WebUntis. After SSO login,
 * the user lands here with ?period_id=X. The page calls the backend
 * resolve endpoint to get the Skyla lesson UUID and redirects to the
 * lesson detail page.
 *
 * Fallback: If the period_id cannot be resolved, redirects to the home page.
 */
const ERROR_REDIRECT_DELAY_MS = 2000;
const UntisRedirect = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const periodId = searchParams.get('period_id');
    useEffect(() => {
        if (!periodId) {
            log('UntisRedirect: No period_id provided, redirecting to home', {
                severity: Severity.WARN,
            });
            navigate(HOME, { replace: true });
            return;
        }
        resolveUntisLesson(periodId)
            .then((response) => {
            if (response.lesson_id) {
                // Success: redirect to the specific lesson
                navigate(getLessonUrl(response.lesson_id), { replace: true });
            }
            else {
                // Lesson not found for this period_id — redirect to home
                log(`UntisRedirect: No lesson found for period_id=${periodId}`, {
                    severity: Severity.WARN,
                });
                navigate(HOME, { replace: true });
            }
        })
            .catch((err) => {
            log('UntisRedirect: Failed to resolve lesson', {
                dump: err,
                severity: Severity.ERROR,
            });
            setError('Stunde konnte nicht gefunden werden.');
            // Redirect to home after a short delay so user sees the error
            setTimeout(() => {
                navigate(HOME, { replace: true });
            }, ERROR_REDIRECT_DELAY_MS);
        });
    }, [periodId, navigate]);
    if (error) {
        return (_jsxs(Stack, { alignItems: "center", justifyContent: "center", spacing: 2, sx: { mt: 8 }, children: [_jsx(Typography, { variant: "h6", color: "error", children: error }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Du wirst zur Hauptseite weitergeleitet..." })] }));
    }
    return (_jsxs(Stack, { alignItems: "center", justifyContent: "center", spacing: 2, sx: { mt: 8 }, children: [_jsx(CircularProgress, {}), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Stunde wird geladen..." })] }));
};
export default UntisRedirect;
